<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import TransactionsModals from "./TransactionsModals.vue";
import emitter from "../assets/js/eventBus";

const store = useStore();
const route = useRoute();

const darkMode = computed(() => store.state.darkMode);

const minimizeSidebar = () => store.commit("sidebarMinimize");

const currentRoot = computed(() => route.path.split("/")[1] || "");

const hiddenNav = computed(() =>
  currentRoot.value === "login" || currentRoot.value === "cadastro"
);

const openType = (type) => {
  emitter.emit("set-type", { data: type });
};
</script>

<template>
  <div
    v-if="!hiddenNav"
    class="mobile-nav d-xl-none"
    :class="{ 'mobile-nav--dark': darkMode }"
  >
    <div class="mobile-nav__surface">
      <router-link to="/inicio" class="mobile-nav__item" :class="{ active: currentRoot === 'inicio' }">
        <span class="mobile-nav__icon-wrap">
          <i class="fa-home" :class="currentRoot === 'inicio' ? 'fas' : 'fal'"></i>
        </span>
        <span class="mobile-nav__label">{{ $t('message.menu.home') }}</span>
      </router-link>

      <router-link to="/transacoes" class="mobile-nav__item" :class="{ active: currentRoot === 'transacoes' }">
        <span class="mobile-nav__icon-wrap">
          <i class="fa-money-bill-wave" :class="currentRoot === 'transacoes' ? 'fas' : 'fal'"></i>
        </span>
        <span class="mobile-nav__label">{{ $t('message.global.transaction', 1) }}</span>
      </router-link>

      <div class="mobile-nav__center">
        <div class="dropup-center dropup">
          <button
            class="mobile-nav__fab"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i class="fal fa-plus"></i>
          </button>

          <ul class="dropdown-menu mobile-nav__menu" :class="{ darkmode: darkMode }">
            <li>
              <a
                class="dropdown-item font-weight-bold"
                :class="darkMode ? 'darkmode' : ''"
                data-bs-toggle="modal"
                data-bs-target="#addTransaction"
                @click="openType('despesa')"
              >
                <i class="fas fa-minus me-2 text-danger"></i>{{ $t('message.global.expense') }}
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <a
                class="dropdown-item font-weight-bold"
                :class="darkMode ? 'darkmode' : ''"
                data-bs-toggle="modal"
                data-bs-target="#addTransaction"
                @click="openType('receita')"
              >
                <i class="fal fa-plus me-2 text-success"></i>{{ $t('message.global.revenue') }}
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <a
                class="dropdown-item font-weight-bold"
                :class="darkMode ? 'darkmode' : ''"
                data-bs-toggle="modal"
                data-bs-target="#addTransaction"
                @click="openType('transferência')"
              >
                <i class="fal fa-exchange-alt me-2 text-primary"></i>{{ $t('message.global.transfer') }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <router-link to="/objetivos" class="mobile-nav__item" :class="{ active: currentRoot === 'objetivos' }">
        <span class="mobile-nav__icon-wrap">
          <i class="fa-bullseye-arrow" :class="currentRoot === 'objetivos' ? 'fas' : 'fal'"></i>
        </span>
        <span class="mobile-nav__label">{{ $t('message.global.objetive') }}</span>
      </router-link>

      <button class="mobile-nav__item mobile-nav__item--button" @click="minimizeSidebar">
        <span class="mobile-nav__icon-wrap">
          <i class="fal fa-bars"></i>
        </span>
        <span class="mobile-nav__label">{{ $t('message.menu.menu') }}</span>
      </button>
    </div>
  </div>

  <TransactionsModals />
</template>

<style scoped>
.mobile-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
  padding: 0 0.85rem 0.7rem;
  pointer-events: none;
}

.mobile-nav__surface {
  pointer-events: auto;
  display: grid;
  grid-template-columns: 1fr 1fr auto 1fr 1fr;
  align-items: end;
  gap: 0.35rem;
  padding: 0.7rem 0.6rem 0.65rem;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow:
    0 14px 40px rgba(15, 23, 42, 0.12),
    0 2px 10px rgba(15, 23, 42, 0.04);
  backdrop-filter: blur(14px);
}

.mobile-nav--dark .mobile-nav__surface {
  background: rgba(15, 23, 42, 0.92);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow:
    0 16px 42px rgba(0, 0, 0, 0.34),
    0 2px 10px rgba(0, 0, 0, 0.16);
}

.mobile-nav__item {
  border: 0;
  background: transparent;
  text-decoration: none;
  color: #7b8faa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.38rem;
  justify-content: center;
  min-height: 54px;
  padding: 0.2rem 0.15rem;
  transition: transform 0.18s ease, color 0.18s ease;
}

.mobile-nav__item--button {
  appearance: none;
}

.mobile-nav__item:active {
  transform: scale(0.98);
}

.mobile-nav__icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
}

.mobile-nav__item i {
  font-size: 1rem;
}

.mobile-nav__label {
  font-size: 0.72rem;
  line-height: 1;
  font-weight: 700;
  text-align: center;
}

.mobile-nav__item.active {
  color: #12395f;
}

.mobile-nav__item.active .mobile-nav__icon-wrap {
  background: rgba(18, 57, 95, 0.10);
  color: #12395f;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.4);
}

.mobile-nav__center {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -1.2rem;
}

.mobile-nav__fab {
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #d18536 0%, #bd7226 100%);
  color: #fff;
  box-shadow:
    0 12px 28px rgba(209, 133, 54, 0.28),
    inset 0 1px 0 rgba(255,255,255,0.22);
}

.mobile-nav__fab i {
  font-size: 1.15rem;
}

.mobile-nav__fab:focus,
.mobile-nav__fab:hover,
.mobile-nav__fab:active {
  background: linear-gradient(180deg, #12395f 0%, #0f2f4e 100%);
  color: #fff;
}

.mobile-nav__menu {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  padding: 0.4rem;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
}

.mobile-nav__menu .dropdown-item {
  border-radius: 12px;
  padding: 0.72rem 0.85rem;
}

.mobile-nav__menu.darkmode {
  background: #172033;
  border-color: rgba(255,255,255,0.08);
}

.mobile-nav__menu.darkmode .dropdown-item {
  color: #f8fafc;
}

.mobile-nav--dark .mobile-nav__item {
  color: #94a3b8;
}

.mobile-nav--dark .mobile-nav__item.active {
  color: #f8fafc;
}

.mobile-nav--dark .mobile-nav__item.active .mobile-nav__icon-wrap {
  background: rgba(255,255,255,0.08);
  color: #f8fafc;
}

@media (max-width: 380px) {
  .mobile-nav__label {
    font-size: 0.68rem;
  }

  .mobile-nav__surface {
    gap: 0.2rem;
    padding-left: 0.45rem;
    padding-right: 0.45rem;
  }
}
</style>
